import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Zap, Clock, ArrowRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FlashSaleBanner = () => {
  const [sales, setSales] = useState([]);
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const fetchSales = async () => {
      try {
        const res = await axios.get(`${API}/flash-sales/active`);
        setSales(res.data);
      } catch (e) { /* no active sales */ }
    };
    fetchSales();
    const interval = setInterval(fetchSales, 60000);
    return () => clearInterval(interval);
  }, []);

  const calcTimeLeft = useCallback(() => {
    const newTimeLeft = {};
    sales.forEach((sale) => {
      const end = new Date(sale.end_time);
      const now = new Date();
      const diff = end - now;
      if (diff > 0) {
        newTimeLeft[sale.sale_id] = {
          hours: Math.floor(diff / (1000 * 60 * 60)),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60)
        };
      }
    });
    setTimeLeft(newTimeLeft);
  }, [sales]);

  useEffect(() => {
    calcTimeLeft();
    const timer = setInterval(calcTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [calcTimeLeft]);

  if (sales.length === 0) return null;

  const sale = sales[0]; // Show the first active sale
  const time = timeLeft[sale.sale_id];
  if (!time) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="relative overflow-hidden"
        data-testid="flash-sale-banner"
      >
        <div className="bg-gradient-to-r from-red-600/20 via-orange-500/20 to-yellow-500/20 border-y border-red-500/30">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <span className="font-heading text-lg uppercase tracking-widest text-yellow-400">Flash Sale</span>
              </div>
              <span className="text-white font-bold">{sale.name}</span>
              <Badge className="bg-red-500 text-white border-0 font-bold text-sm">
                {sale.discount_percent}% OFF
              </Badge>
            </div>

            {/* Countdown */}
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-red-400" />
              <div className="flex gap-1 font-mono">
                <div className="bg-black/60 px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-white font-bold text-lg">{String(time.hours).padStart(2, '0')}</span>
                  <p className="text-[9px] text-zinc-500 uppercase">hrs</p>
                </div>
                <span className="text-red-400 text-lg font-bold self-start mt-1">:</span>
                <div className="bg-black/60 px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-white font-bold text-lg">{String(time.minutes).padStart(2, '0')}</span>
                  <p className="text-[9px] text-zinc-500 uppercase">min</p>
                </div>
                <span className="text-red-400 text-lg font-bold self-start mt-1">:</span>
                <div className="bg-black/60 px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-white font-bold text-lg">{String(time.seconds).padStart(2, '0')}</span>
                  <p className="text-[9px] text-zinc-500 uppercase">sec</p>
                </div>
              </div>

              <Link to={sale.applies_to === "beats" ? "/beats" : sale.applies_to === "merch" ? "/merch" : "/beats"}>
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase text-xs">
                  Shop Now <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FlashSaleBanner;
